import { inject, Injectable } from '@angular/core';
import { DisruptionGridFiltersService } from '../disruption-grid-filters.service';
import {
  EInitEventDisruptionMetroTrackClosureType,
  EInitEventDisruptionProtocolStatusCode,
  EInitEventStateCode,
  IInitEventDisruptionGridCount,
  InitEventDisruptionApiService,
  EInitEventTypeCode,
  EInfrastructureTypeCode,
} from 'ngx-mt-ui-lib/api-client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, EMPTY, filter, from, map, of, switchMap, tap } from 'rxjs';
import { ToastService } from 'ngx-mt-ui-lib/services/toast';
import { TranslateService } from '@ngx-translate/core';
import { throttleTime, withLatestFrom } from 'rxjs/operators';
import { IAppState } from '@app/app.state';
import { Store } from '@ngrx/store';
import { Dialog } from '@angular/cdk/dialog';
import { DisruptionActions } from './disruption.actions';
import { toDisruptionCreateRequest } from './disruption.mappers';
import { IDisruptionTrackClosureItem } from '@app/core/interfaces/disruption.interface';
import {
  DialogComponent,
  IDialogComponentData,
  openConfirmDialog,
} from 'ngx-mt-ui-lib/components/dialog';
import { ISelectItem } from 'ngx-mt-ui-lib/interfaces';

import { Router } from '@angular/router';
import {
  selectActiveProtocol,
  selectDisruptionHasChanges,
  selectDisruptionId,
  selectDisruptionInfo,
} from './disruption.selectors';
import { ReasonDialogComponent } from '@app/shared/components/reason-dialog/reason-dialog.component';
import {
  IReasonDialogData,
  IReasonDialogResult,
} from '@app/core/interfaces/reason-dialog.interface';
import { openLazyDialog } from '@app/shared/functions/open-lazy-dialog.function';
import { parseError } from 'ngx-mt-ui-lib/functions';

import { EInitEventCardUrl } from '@app/app.enums';
import {
  formatDurationForMetroTrackClosure,
  calcDuration,
} from '@app/shared/functions/format-duration.function';

@Injectable()
export class DisruptionEffects {
  protected readonly dialog = inject(Dialog);
  protected readonly disruptionGridService = inject(
    DisruptionGridFiltersService,
  );

  private readonly store = inject(Store<IAppState>);
  private readonly disruptionApiService = inject(InitEventDisruptionApiService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);

  readonly selectItem$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.selectItem),
        tap((action) => {
          if (typeof action?.id === 'number') {
            this.store.dispatch(
              DisruptionActions.requestItemInfo({ id: action.id }),
            );
          }
        }),
      ),
    { dispatch: false },
  );

  readonly requestItemInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestItemInfo),
      throttleTime(1000),
      switchMap((action) => {
        const api = this.disruptionApiService.getInfo(action.id);
        return api.pipe(
          map((item) =>
            DisruptionActions.requestItemInfoSuccess({
              item,
            }),
          ),
          catchError((error) =>
            of(DisruptionActions.requestItemInfoError({ error })),
          ),
        );
      }),
    ),
  );

  readonly requestItemInfoSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestItemInfoSuccess),
        tap(({ item }) => {
          if (
            item?.facilityInfraOwnerTypeCode &&
            item?.facilityInfraOwnerTypeCode === EInfrastructureTypeCode.METRO
          ) {
            this.store.dispatch(DisruptionActions.requestTrackClosureList());
          }
          this.store.dispatch(DisruptionActions.requestChildDisruptionList());
          this.store.dispatch(DisruptionActions.requestTags());
        }),
      ),
    { dispatch: false },
  );

  readonly requestCountForState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestCountForState),
      switchMap((action) =>
        this.disruptionApiService.getCountForStates(action.body).pipe(
          map((counts) =>
            DisruptionActions.requestCountForStateSuccess({
              counts,
            }),
          ),
          catchError((error) =>
            of(DisruptionActions.requestCountForStateError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestCountForStateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestCountForStateSuccess),
      switchMap((action) => {
        const list: ISelectItem<{ count: number }, string>[] =
          this.getStateList(action?.counts);
        return of(DisruptionActions.setCountForState({ list }));
      }),
    ),
  );

  readonly requestCountForStateError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestCountForStateError),
      switchMap(() => {
        const list: ISelectItem<{ count: number }, string>[] =
          this.getStateList();
        return of(DisruptionActions.setCountForState({ list }));
      }),
    ),
  );

  private completeProtocolStep(protocolStepId?: number): void {
    if (typeof protocolStepId !== 'number') {
      return;
    }

    this.store.dispatch(
      DisruptionActions.requestProtocolStepsComplete({
        stepId: protocolStepId,
        completedByOther: false,
      }),
    );
  }

  private getStateList(
    counts: IInitEventDisruptionGridCount = null,
  ): ISelectItem<{ count: number }, string>[] {
    const states = this.translateService.instant('EVENT.DISRUPTION.STATE_LIST');
    return [
      {
        key: null,
        value: states['ALL'],
        ext: { count: counts?.totalCount ?? 0 },
      },
      {
        key: EInitEventStateCode.NEW,
        value: states['NEW'],
        ext: { count: counts?.newCount ?? 0 },
      },
      {
        key: EInitEventStateCode.WORKING,
        value: states['WORKING'],
        ext: { count: counts?.workingCount ?? 0 },
      },
    ];
  }

  readonly openCreateItemCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.openCreateItemCard),
      switchMap(
        ({
          parentId,
          prefill,
          sourceMessage,
          withConfirmDialog,
          confirmDialogTitle,
        }) => {
          return openLazyDialog(
            this.dialog,
            () =>
              import('../add-disruption-card/add-disruption-card.component'),
            'AddDisruptionCardComponent',
            {
              autoFocus: false,
              panelClass: 'aligned-right-dialog',
              data: {
                prefill,
                withConfirmDialog,
                confirmDialogTitle,
              },
            },
          ).pipe(
            switchMap((res) => {
              if (!res) {
                return EMPTY;
              }

              return of(
                DisruptionActions.requestCreateItem({
                  params: {
                    parentId,
                    sourceMessage,
                    description: res?.['description'] ?? null,
                    facilityInfraOwnerTypeCode:
                      res?.['facilityInfraOwnerTypeCode'] ?? null,
                    stationId: res?.['stationId'] ?? null,
                    startedAt: res?.['startedAt'] ?? null,
                    protocolTemplateId: res?.['protocolTemplateId'] ?? null,
                    userAddress: res?.['userAddress'] ?? null,
                    userLatitude: res?.['userLatitude'] ?? null,
                    userLongitude: res?.['userLongitude'] ?? null,
                  },
                }),
              );
            }),
          );
        },
      ),
    ),
  );

  readonly requestCreateItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestCreateItem),
      switchMap((action) => {
        const req$ = this.disruptionApiService.create(
          toDisruptionCreateRequest(action.params),
        );
        return req$.pipe(
          map((result) =>
            DisruptionActions.requestCreateItemSuccess({
              id: result?.id ?? null,
            }),
          ),
          catchError((error) =>
            of(DisruptionActions.requestCreateItemError({ error })),
          ),
        );
      }),
    ),
  );

  readonly requestCreateItemSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestCreateItemSuccess),
        tap((action) => {
          this.disruptionGridService.refreshGrid();
          this.router.navigate([`${EInitEventCardUrl.NNR}/${action.id}`]);
        }),
      ),
    { dispatch: false },
  );

  readonly requestUpdateItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestUpdateItem),
      switchMap((action) => {
        const api = this.disruptionApiService.update(
          action?.id,
          action?.params,
        );
        return api.pipe(
          map((result) =>
            DisruptionActions.requestUpdateItemSuccess({
              item: result,
            }),
          ),
          catchError((error) =>
            of(DisruptionActions.requestUpdateItemError({ error })),
          ),
        );
      }),
    ),
  );

  readonly openAddParentDisruptionModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.openAddParentDisruptionModal),
        switchMap(({ disruptionId }) =>
          openLazyDialog(
            this.dialog,
            () =>
              import('../../shared/add-parent-disruption-modal/add-parent-disruption-modal.component'),
            'AddParentDisruptionModalComponent',
            {
              autoFocus: false,
              data: {
                eventId: disruptionId,
                eventTypeCode: EInitEventTypeCode.NNR,
              },
              panelClass: 'aligned-right-dialog',
            },
          ),
        ),
      ),
    { dispatch: false },
  );

  readonly attemptRequestAddParentDisruption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptRequestAddParentDisruption),
      withLatestFrom(this.store.select(selectDisruptionHasChanges)),
      switchMap(([params, hasChanges]) => {
        if (!hasChanges) {
          return of(DisruptionActions.requestAddParentDisruption(params));
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(DisruptionActions.requestAddParentDisruption(params));
          }),
        );
      }),
    ),
  );

  readonly requestAddParentDisruption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestAddParentDisruption),
      switchMap(({ disruptionId, disruptionLinkId }) =>
        this.disruptionApiService
          .addParentDisruption(disruptionId, disruptionLinkId)
          .pipe(
            map(() => DisruptionActions.requestAddParentDisruptionSuccess()),
            catchError((error) =>
              of(
                DisruptionActions.requestAddParentDisruptionError({
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly requestAddParentDisruptionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestAddParentDisruptionSuccess),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([_, id]) => of(DisruptionActions.requestItemInfo({ id }))),
    ),
  );

  readonly openAddParentSecurityEventModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.openAddParentSecurityEventModal),
        switchMap(({ disruptionId }) =>
          openLazyDialog(
            this.dialog,
            () =>
              import('../../shared/add-parent-security-event-modal/add-parent-security-event-modal.component'),
            'AddParentSecurityEventModalComponent',
            {
              autoFocus: false,
              data: {
                eventId: disruptionId,
                eventTypeCode: EInitEventTypeCode.NNR,
              },
              panelClass: 'aligned-right-dialog',
            },
          ),
        ),
      ),
    { dispatch: false },
  );

  readonly attemptRequestAddParentSecurityEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptRequestAddParentSecurityEvent),
      withLatestFrom(this.store.select(selectDisruptionHasChanges)),
      switchMap(([params, hasChanges]) => {
        if (!hasChanges) {
          return of(DisruptionActions.requestAddParentSecurityEvent(params));
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(DisruptionActions.requestAddParentSecurityEvent(params));
          }),
        );
      }),
    ),
  );

  readonly requestAddParentSecurityEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestAddParentSecurityEvent),
      switchMap(({ disruptionId, securityEventLinkId }) =>
        this.disruptionApiService
          .addParentSecurityEvent(disruptionId, securityEventLinkId)
          .pipe(
            map(() => DisruptionActions.requestAddParentSecurityEventSuccess()),
            catchError((error) =>
              of(
                DisruptionActions.requestAddParentSecurityEventError({
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly requestAddParentSecurityEventSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestAddParentSecurityEventSuccess),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([_, id]) => of(DisruptionActions.requestItemInfo({ id }))),
    ),
  );

  readonly openAddChildDisruptionModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.openAddChildDisruptionModal),
        switchMap(({ disruptionId }) =>
          openLazyDialog(
            this.dialog,
            () =>
              import('../../shared/add-child-disruption-modal/add-child-disruption-modal.component'),
            'AddChildDisruptionModalComponent',
            {
              autoFocus: false,
              data: {
                eventId: disruptionId,
                eventTypeCode: EInitEventTypeCode.NNR,
              },
              panelClass: 'aligned-right-dialog',
            },
          ),
        ),
      ),
    { dispatch: false },
  );

  readonly attemptRequestAddChildDisruption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptRequestAddChildDisruption),
      withLatestFrom(this.store.select(selectDisruptionHasChanges)),
      switchMap(([params, hasChanges]) => {
        if (!hasChanges) {
          return of(DisruptionActions.requestAddChildDisruption(params));
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(DisruptionActions.requestAddChildDisruption(params));
          }),
        );
      }),
    ),
  );

  readonly requestAddChildDisruption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestAddChildDisruption),
      switchMap(({ disruptionId, disruptionLinkId }) =>
        this.disruptionApiService
          .addChildDisruption(disruptionId, disruptionLinkId)
          .pipe(
            map(() => DisruptionActions.requestAddChildDisruptionSuccess()),
            catchError((error) =>
              of(
                DisruptionActions.requestAddChildDisruptionError({
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly requestAddChildDisruptionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestAddChildDisruptionSuccess),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([_, id]) => of(DisruptionActions.requestItemInfo({ id }))),
    ),
  );

  readonly requestDeleteChildDisruption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestDeleteChildDisruption),
      switchMap(({ disruptionId, disruptionLinkId }) =>
        this.disruptionApiService
          .deleteChildDisruption(disruptionId, disruptionLinkId)
          .pipe(
            map(() => DisruptionActions.requestDeleteChildDisruptionSuccess()),
            catchError((error) =>
              of(
                DisruptionActions.requestDeleteChildDisruptionError({
                  error,
                  eventLinkId: disruptionLinkId,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly requestDeleteChildDisruptionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestDeleteChildDisruptionSuccess),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([_, id]) => of(DisruptionActions.requestItemInfo({ id }))),
    ),
  );

  readonly requestTrackClosureList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestTrackClosureList),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([_, disruptionId]) =>
        this.disruptionApiService.getTrackClosureList(disruptionId).pipe(
          map((metroTrackClosureList: IDisruptionTrackClosureItem[]) =>
            DisruptionActions.requestTrackClosureListSuccess({
              metroTrackClosureList: this.prepareTrackClosureList(
                metroTrackClosureList,
              ),
            }),
          ),
          catchError((error) =>
            of(DisruptionActions.requestTrackClosureListError({ error })),
          ),
        ),
      ),
    ),
  );

  private prepareTrackClosureList(
    metroTrackClosureList: IDisruptionTrackClosureItem[],
  ): IDisruptionTrackClosureItem[] {
    const texts = this.translateService.instant('NO_MOVEMENT');
    return metroTrackClosureList?.length > 0
      ? metroTrackClosureList.map((item: IDisruptionTrackClosureItem) => {
          const duration = calcDuration(item.startDate, item.endDate);
          const durationFormated = formatDurationForMetroTrackClosure(
            duration,
            this.translateService,
          );
          const closureType =
            item.trackMainNumber === 'I'
              ? EInitEventDisruptionMetroTrackClosureType.TRACK_I
              : EInitEventDisruptionMetroTrackClosureType.TRACK_II;
          const closureText = texts[closureType];
          return {
            ...item,
            ...{ duration, durationFormated, closureType, closureText },
          };
        })
      : [];
  }

  readonly openAddTrackClosureModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.openAddTrackClosureModal),
        switchMap(({ disruptionId }) =>
          openLazyDialog(
            this.dialog,
            () =>
              import('../add-disruption-track-closure-modal/add-disruption-track-closure-modal.component'),
            'AddDisruptionTrackClosureModalComponent',
            {
              autoFocus: false,
              data: {
                disruptionId,
              },
              panelClass: 'aligned-right-dialog',
            },
          ),
        ),
      ),
    { dispatch: false },
  );

  readonly attemptRequestAddTrackClosure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptRequestAddTrackClosure),
      withLatestFrom(this.store.select(selectDisruptionHasChanges)),
      switchMap(([{ disruptionId, body }, hasChanges]) => {
        if (!hasChanges) {
          return of(
            DisruptionActions.requestAddTrackClosure({ disruptionId, body }),
          );
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              DisruptionActions.requestAddTrackClosure({ disruptionId, body }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestAddTrackClosure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestAddTrackClosure),
      switchMap(({ disruptionId, body }) =>
        this.disruptionApiService.addTrackClosure(disruptionId, body).pipe(
          map(() => DisruptionActions.requestAddTrackClosureSuccess()),
          catchError((error) =>
            of(
              DisruptionActions.requestAddTrackClosureError({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly requestAddTrackClosureSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestAddTrackClosureSuccess),
        tap(() => {
          this.store.dispatch(DisruptionActions.requestTrackClosureList());
        }),
      ),
    { dispatch: false },
  );

  readonly attemptRequestCompleteTrackClosure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptRequestCompleteTrackClosure),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([action, disruptionId]) => {
        if (!disruptionId || !action?.metroTrackClosureId) {
          return EMPTY;
        }

        const ref = this.dialog.open<
          boolean,
          IDialogComponentData,
          DialogComponent
        >(DialogComponent, {
          autoFocus: false,
          data: {
            content:
              'EVENT.DISRUPTION.CARD.FUNCTIONS.COMPLETE_TRACK_CLOSURE.CONFIRM',
            width: '28.75rem',
          },
        });

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              DisruptionActions.requestCompleteTrackClosure({
                disruptionId,
                metroTrackClosureId: action?.metroTrackClosureId,
              }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestCompleteTrackClosure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestCompleteTrackClosure),
      switchMap(({ disruptionId, metroTrackClosureId }) =>
        this.disruptionApiService
          .completeTrackClosure(disruptionId, metroTrackClosureId)
          .pipe(
            map(() => DisruptionActions.requestCompleteTrackClosureSuccess()),
            catchError((error) =>
              of(
                DisruptionActions.requestCompleteTrackClosureError({
                  error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly requestCompleteTrackClosureSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestCompleteTrackClosureSuccess),
        tap(() => {
          this.store.dispatch(DisruptionActions.requestTrackClosureList());
        }),
      ),
    { dispatch: false },
  );

  readonly attemptSetToWorking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptSetToWorking),
      withLatestFrom(this.store.select(selectDisruptionHasChanges)),
      switchMap(([action, hasChanges]) => {
        if (!action?.id) {
          return EMPTY;
        }
        if (!hasChanges) {
          return of(DisruptionActions.requestSetToWorking({ id: action.id }));
        }
        const ref = openConfirmDialog(this.dialog);
        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }
            return of(DisruptionActions.requestSetToWorking({ id: action.id }));
          }),
        );
      }),
    ),
  );

  readonly requestSetToWorking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestSetToWorking),
      switchMap((action) => {
        const req$ = this.disruptionApiService.setToWorking(action.id);
        return req$.pipe(
          map(() =>
            DisruptionActions.requestSetToWorkingSuccess({ id: action.id }),
          ),
          catchError((error) =>
            of(DisruptionActions.requestSetToWorkingError({ error })),
          ),
        );
      }),
    ),
  );

  readonly attemptSetToCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptSetToCompleted),
      withLatestFrom(this.store.select(selectDisruptionHasChanges)),
      switchMap(([action, hasChanges]) => {
        if (!action?.id) {
          return EMPTY;
        }
        if (!hasChanges) {
          return of(DisruptionActions.requestSetToCompleted({ id: action.id }));
        }
        const ref = openConfirmDialog(this.dialog);
        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }
            return of(
              DisruptionActions.requestSetToCompleted({ id: action.id }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestSetToCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestSetToCompleted),
      switchMap((action) => {
        const req$ = this.disruptionApiService.setToCompleted(action.id);
        return req$.pipe(
          map(() =>
            DisruptionActions.requestSetToCompletedSuccess({ id: action.id }),
          ),
          catchError((error) =>
            of(DisruptionActions.requestSetToCompletedError({ error })),
          ),
        );
      }),
    ),
  );

  readonly requestSetToCompletedSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestSetToCompletedSuccess),
        withLatestFrom(this.store.select(selectActiveProtocol)),
        tap(([_, { id, stateCode }]) => {
          if (
            typeof id == 'number' &&
            stateCode ===
              EInitEventDisruptionProtocolStatusCode.DISP_PROTOCOL_WORKING
          ) {
            this.store.dispatch(
              DisruptionActions.requestCancelProtocol({ protocolId: id }),
            );
          }
        }),
      ),
    { dispatch: false },
  );

  readonly requestSetToClose$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestSetToClose),
      switchMap((action) => {
        const req$ = this.disruptionApiService.setToClose(action.id);
        return req$.pipe(
          map(() =>
            DisruptionActions.requestSetToCloseSuccess({ id: action.id }),
          ),
          catchError((error) =>
            of(DisruptionActions.requestSetToCloseError({ error })),
          ),
        );
      }),
    ),
  );

  readonly attemptSetToCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptSetToCancel),
      switchMap(({ id }) => {
        if (!id) {
          return EMPTY;
        }

        const ref = this.dialog.open<
          IReasonDialogResult,
          IReasonDialogData,
          ReasonDialogComponent
        >(ReasonDialogComponent, {
          autoFocus: false,
          data: {
            title: this.translateService.instant(
              'EVENT.DISRUPTION.CARD.FUNCTIONS.SET_TO_CANCEL.TITLE',
            ),
          },
        });

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              DisruptionActions.requestSetToCancel({ id, reason: res.reason }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestSetToCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestSetToCancel),
      switchMap(({ id, reason }) =>
        this.disruptionApiService.setToCancel(id, reason).pipe(
          map(() => DisruptionActions.requestSetToCancelSuccess({ id })),
          catchError((error) =>
            of(DisruptionActions.requestSetToCancelError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestSetToArchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestSetToArchive),
      switchMap((action) => {
        const req$ = this.disruptionApiService.setToArchive(action.id);
        return req$.pipe(
          map(() =>
            DisruptionActions.requestSetToArchiveSuccess({ id: action.id }),
          ),
          catchError((error) =>
            of(DisruptionActions.requestSetToArchiveError({ error })),
          ),
        );
      }),
    ),
  );

  readonly attemptFixCompensationRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptFixCompensationRoute),
      switchMap(({ id, protocolStepId }) => {
        if (!id) {
          return EMPTY;
        }

        const ref = this.dialog.open<
          boolean,
          IDialogComponentData,
          DialogComponent
        >(DialogComponent, {
          autoFocus: false,
          data: {
            title:
              'EVENT.DISRUPTION.CARD.FUNCTIONS.FIX_COMPENSATION_ROUTE.CONFIRM_TITLE',
            content:
              'EVENT.DISRUPTION.CARD.FUNCTIONS.FIX_COMPENSATION_ROUTE.CONFIRM',
            width: '28.75rem',
          },
        });

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(
              DisruptionActions.requestFixCompensationRoute({
                id,
                protocolStepId,
              }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestFixCompensationRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestFixCompensationRoute),
      switchMap((action) => {
        const req$ = this.disruptionApiService.fixCompensationRoute(action.id);
        return req$.pipe(
          map(() =>
            DisruptionActions.requestFixCompensationRouteSuccess({
              protocolStepId: action.protocolStepId,
            }),
          ),
          catchError((error) =>
            of(DisruptionActions.requestFixCompensationRouteError({ error })),
          ),
        );
      }),
    ),
  );

  readonly requestFixCompensationRouteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestFixCompensationRouteSuccess),
        withLatestFrom(this.store.select(selectDisruptionId)),
        tap(([action, id]) => {
          if (typeof id == 'number') {
            this.store.dispatch(DisruptionActions.requestItemInfo({ id }));
          }
          this.completeProtocolStep(action.protocolStepId);
        }),
      ),
    { dispatch: false },
  );

  readonly updateInfoAfterChangeState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DisruptionActions.requestSetToWorkingSuccess,
        DisruptionActions.requestSetToCompletedSuccess,
        DisruptionActions.requestSetToCloseSuccess,
        DisruptionActions.requestSetToCancelSuccess,
        DisruptionActions.requestSetToArchiveSuccess,
      ),
      tap(() => {
        this.disruptionGridService.refreshGrid();
      }),
      switchMap((action) => {
        return of(DisruptionActions.requestItemInfo({ id: action.id }));
      }),
    ),
  );

  readonly requestTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestTags),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([_, disruptionId]) =>
        this.disruptionApiService.getTags(disruptionId).pipe(
          map((tags) =>
            DisruptionActions.requestTagsSuccess({
              tags,
            }),
          ),
          catchError((error) =>
            of(DisruptionActions.requestTagsError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestEditTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestEditTags),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([{ tagIds }, disruptionId]) =>
        this.disruptionApiService.editTags(disruptionId, tagIds).pipe(
          map(() => DisruptionActions.requestEditTagsSuccess()),
          catchError((error) =>
            of(DisruptionActions.requestEditTagsError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestEditTagsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestEditTagsSuccess),
        tap(() => this.store.dispatch(DisruptionActions.requestTags())),
      ),
    { dispatch: false },
  );

  readonly requestDeleteSystemTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestDeleteSystemTag),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([{ rmmInitEventTagId }, disruptionId]) =>
        this.disruptionApiService
          .deleteSystemTag(disruptionId, rmmInitEventTagId)
          .pipe(
            map(() => DisruptionActions.requestDeleteSystemTagSuccess()),
            catchError((error) =>
              of(DisruptionActions.requestDeleteSystemTagError({ error })),
            ),
          ),
      ),
    ),
  );

  readonly requestDeleteSystemTagSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestDeleteSystemTagSuccess),
        tap(() => this.store.dispatch(DisruptionActions.requestTags())),
      ),
    { dispatch: false },
  );

  readonly requestChildDisruptionList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestChildDisruptionList),
      withLatestFrom(this.store.select(selectDisruptionId)),
      switchMap(([_, disruptionId]) =>
        this.disruptionApiService
          .getLinkChildrenDisruptionList(disruptionId)
          .pipe(
            map((childDisruptionList) =>
              DisruptionActions.requestChildDisruptionListSuccess({
                childDisruptionList: childDisruptionList,
              }),
            ),
            catchError((error) =>
              of(DisruptionActions.requestChildDisruptionListError({ error })),
            ),
          ),
      ),
    ),
  );

  readonly attemptGoToReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptGoToReport),
      withLatestFrom(this.store.select(selectDisruptionHasChanges)),
      switchMap(([_, hasChanges]) => {
        if (!hasChanges) {
          return of(DisruptionActions.goToReport());
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(DisruptionActions.goToReport());
          }),
        );
      }),
    ),
  );

  readonly goToReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.goToReport),
        withLatestFrom(this.store.select(selectDisruptionId)),
        tap(([_, id]) =>
          this.router.navigate([`${EInitEventCardUrl.NNR}/${id}/report`], {
            queryParamsHandling: 'preserve',
          }),
        ),
      ),
    { dispatch: false },
  );

  readonly attemptGoToHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptGoToHistory),
      withLatestFrom(this.store.select(selectDisruptionHasChanges)),
      switchMap(([_, hasChanges]) => {
        if (!hasChanges) {
          return of(DisruptionActions.goToHistory());
        }

        return openConfirmDialog(this.dialog).closed.pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(DisruptionActions.goToHistory());
          }),
        );
      }),
    ),
  );

  readonly goToHistory$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.goToHistory),
        withLatestFrom(this.store.select(selectDisruptionId)),
        tap(([_, id]) =>
          this.router.navigate([`${EInitEventCardUrl.NNR}/${id}/history`], {
            queryParamsHandling: 'preserve',
          }),
        ),
      ),
    { dispatch: false },
  );

  readonly attemptGoToGanttDiagram$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptGoToGanttDiagram),
      withLatestFrom(this.store.select(selectDisruptionHasChanges)),
      switchMap(([_, hasChanges]) => {
        if (!hasChanges) {
          return of(DisruptionActions.goToGanttDiagram());
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(DisruptionActions.goToGanttDiagram());
          }),
        );
      }),
    ),
  );

  readonly goToGanttDiagram$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.goToGanttDiagram),
        withLatestFrom(this.store.select(selectDisruptionId)),
        tap(([_, id]) =>
          this.router.navigate(
            [`${EInitEventCardUrl.NNR}/${id}/gantt-diagram`],
            { queryParamsHandling: 'preserve' },
          ),
        ),
      ),
    { dispatch: false },
  );

  readonly attemptShowOnGisMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptShowOnGisMap),
      withLatestFrom(this.store.select(selectDisruptionHasChanges)),
      switchMap(([_, hasChanges]) => {
        if (!hasChanges) {
          return of(DisruptionActions.showOnGisMap());
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(DisruptionActions.showOnGisMap());
          }),
        );
      }),
    ),
  );

  readonly showOnGisMap$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.showOnGisMap),
        withLatestFrom(this.store.select(selectDisruptionInfo)),
        tap(([_, { stationId }]) => {
          if (!stationId) {
            return;
          }

          window.open(
            `${document.location.origin}/map/city?selectStationId=${stationId}`,
            '_blank',
          );
        }),
      ),
    { dispatch: false },
  );

  readonly attemptShowOnMetroMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptShowOnMetroMap),
      withLatestFrom(this.store.select(selectDisruptionHasChanges)),
      switchMap(([_, hasChanges]) => {
        if (!hasChanges) {
          return of(DisruptionActions.showOnMetroMap());
        }

        const ref = openConfirmDialog(this.dialog);

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            return of(DisruptionActions.showOnMetroMap());
          }),
        );
      }),
    ),
  );

  readonly showOnMetroMap$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.showOnMetroMap),
        withLatestFrom(this.store.select(selectDisruptionInfo)),
        tap(([_, { stationId }]) => {
          if (!stationId) {
            return;
          }

          window.open(
            `${document.location.origin}/map/metro?selectStationId=${stationId}`,
            '_blank',
          );
        }),
      ),
    { dispatch: false },
  );

  readonly attemptToggleOnWall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.attemptToggleOnWall),
      switchMap(({ info }) => {
        if (!info) {
          return EMPTY;
        }

        const title = this.translateService.instant(
          info.isBroadcast
            ? 'EVENT.DISRUPTION.CARD.FUNCTIONS.HIDE_FROM_WALL.TITLE'
            : 'EVENT.DISRUPTION.CARD.FUNCTIONS.SHOW_ON_WALL.TITLE',
        );
        const content = this.translateService.instant(
          info.isBroadcast
            ? 'EVENT.DISRUPTION.CARD.FUNCTIONS.HIDE_FROM_WALL.CONFIRM'
            : 'EVENT.DISRUPTION.CARD.FUNCTIONS.SHOW_ON_WALL.CONFIRM',
        );

        const ref = this.dialog.open<
          boolean,
          IDialogComponentData,
          DialogComponent
        >(DialogComponent, {
          autoFocus: false,
          data: {
            title,
            content,
            width: '35rem',
            buttons: [
              {
                title: 'GENERAL.CANCEL',
                styledEmphasis: 'emphasis-button-ghost',
                onClick: () => false,
              },
              {
                title: 'GENERAL.CONFIRM',
                onClick: () => true,
              },
            ],
          },
        });

        return from(ref.closed).pipe(
          switchMap((res) => {
            if (!res) {
              return EMPTY;
            }

            if (info.isBroadcast) {
              return of(
                DisruptionActions.requestHideFromWall({
                  disruptionId: info.id,
                }),
              );
            }

            return of(
              DisruptionActions.requestShowOnWall({
                disruptionId: info.id,
              }),
            );
          }),
        );
      }),
    ),
  );

  readonly requestShowOnWall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestShowOnWall),
      switchMap(({ disruptionId }) =>
        this.disruptionApiService.showOnWall(disruptionId).pipe(
          map(() => DisruptionActions.requestShowOnWallSuccess()),
          catchError((error) =>
            of(
              DisruptionActions.requestShowOnWallError({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly requestShowOnWallSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestShowOnWallSuccess),
        withLatestFrom(this.store.select(selectDisruptionId)),
        tap(([_, id]) => {
          this.store.dispatch(DisruptionActions.requestItemInfo({ id }));
        }),
      ),
    { dispatch: false },
  );

  readonly requestHideFromWall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DisruptionActions.requestHideFromWall),
      switchMap(({ disruptionId }) =>
        this.disruptionApiService.hideFromWall(disruptionId).pipe(
          map(() => DisruptionActions.requestHideFromWallSuccess()),
          catchError((error) =>
            of(
              DisruptionActions.requestHideFromWallError({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly requestHideFromWallSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestHideFromWallSuccess),
        withLatestFrom(this.store.select(selectDisruptionId)),
        tap(([_, id]) => {
          this.store.dispatch(DisruptionActions.requestItemInfo({ id }));
        }),
      ),
    { dispatch: false },
  );

  readonly showSucces$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DisruptionActions.requestCreateItemSuccess,
          DisruptionActions.requestUpdateItemSuccess,
          DisruptionActions.requestAddPvsSuccess,
          DisruptionActions.requestAddParentDisruptionSuccess,
          DisruptionActions.requestAddParentSecurityEventSuccess,
          DisruptionActions.requestAddChildDisruptionSuccess,
          DisruptionActions.requestDeleteChildDisruptionSuccess,
          DisruptionActions.requestSetToWorkingSuccess,
          DisruptionActions.requestSetToCompletedSuccess,
          DisruptionActions.requestSetToCloseSuccess,
          DisruptionActions.requestSetToCancelSuccess,
          DisruptionActions.requestSetToArchiveSuccess,
          DisruptionActions.requestFixCompensationRouteSuccess,
          DisruptionActions.requestRunProtocolSuccess,
          DisruptionActions.requestCancelProtocolSuccess,
          DisruptionActions.requestAddProtocolStepSuccess,
          DisruptionActions.requestProtocolStepsDeleteSuccess,
          DisruptionActions.requestProtocolStepsCompleteSuccess,
          DisruptionActions.requestProtocolStepsCancelSuccess,
          DisruptionActions.requestSetPvsTaskToCancelSuccess,
          DisruptionActions.requestSetPvsTaskToCompleteSuccess,
          DisruptionActions.requestShowOnWallSuccess,
          DisruptionActions.requestHideFromWallSuccess,
          DisruptionActions.requestAddTrackClosureSuccess,
          DisruptionActions.requestCompleteTrackClosureSuccess,
          DisruptionActions.requestEditTagsSuccess,
          DisruptionActions.requestDeleteSystemTagSuccess,
        ),
        tap((action) => {
          let successTitle: string = null;

          switch (action.type) {
            case '[DISRUPTION] requestCreateItemSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.ADD_CARD.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestUpdateItemSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.UPDATE.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestAddPvsSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.ADD_PVS.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestAddParentDisruptionSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.ADD_PARENT_DISRUPTION.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestAddParentSecurityEventSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.ADD_PARENT_SECURITY_EVENT.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestAddChildDisruptionSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.ADD_CHILD_DISRUPTION.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestDeleteChildDisruptionSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.DELETE_CHILD_DISRUPTION.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestSetToWorkingSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.SET_TO_WORKING.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestSetToCompletedSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.SET_TO_COMPLETED.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestSetToCloseSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.SET_TO_CLOSE.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestSetToCancelSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.SET_TO_CANCEL.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestSetToArchiveSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.SET_TO_ARCHIVE.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestFixCompensationRouteSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.FIX_COMPENSATION_ROUTE.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestRunProtocolSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.RUN.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestCancelProtocolSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.CANCEL.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestAddProtocolStepSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.ADD_STEP.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestProtocolStepsDeleteSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.DELETE_STEP.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestProtocolStepsCompleteSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.COMPLETE_STEP.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestProtocolStepsCancelSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.CANCEL_STEP.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestSetPvsTaskToCancelSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PVS_CALL.FUNCTIONS.SET_TO_CANCEL.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestSetPvsTaskToCompleteSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PVS_CALL.FUNCTIONS.SET_TO_COMPLETED.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestShowOnWallSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.SHOW_ON_WALL.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestHideFromWallSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.HIDE_FROM_WALL.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestAddTrackClosureSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.ADD_TRACK_CLOSURE.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestCompleteTrackClosureSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.COMPLETE_TRACK_CLOSURE.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestEditTagsSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.EDIT_TAGS.SUCCESS',
              );
              break;
            case '[DISRUPTION] requestDeleteSystemTagSuccess':
              successTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.EDIT_TAGS.SUCCESS',
              );
              break;
            default:
              successTitle = this.translateService.instant(
                'GENERAL.SUCCESS_SAVE',
              );
          }

          this.toastService.add({ level: 'success', summary: successTitle });
        }),
      ),
    { dispatch: false },
  );

  readonly showErrors$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DisruptionActions.requestCreateItemError,
          DisruptionActions.requestUpdateItemError,
          DisruptionActions.requestItemInfoError,
          DisruptionActions.requestAddPvsError,
          DisruptionActions.requestAddParentDisruptionError,
          DisruptionActions.requestAddParentSecurityEventError,
          DisruptionActions.requestAddChildDisruptionError,
          DisruptionActions.requestSetToWorkingError,
          DisruptionActions.requestSetToCompletedError,
          DisruptionActions.requestSetToCloseError,
          DisruptionActions.requestSetToCancelError,
          DisruptionActions.requestSetToArchiveError,
          DisruptionActions.requestFixCompensationRouteError,
          DisruptionActions.requestRunProtocolError,
          DisruptionActions.requestProtocolsError,
          DisruptionActions.requestCancelProtocolError,
          DisruptionActions.requestAddProtocolStepError,
          DisruptionActions.requestProtocolStepsDeleteError,
          DisruptionActions.requestProtocolStepsCompleteError,
          DisruptionActions.requestProtocolStepsCancelError,
          DisruptionActions.requestSetPvsTaskToCancelError,
          DisruptionActions.requestSetPvsTaskToCompleteError,
          DisruptionActions.requestShowOnWallError,
          DisruptionActions.requestHideFromWallError,
          DisruptionActions.requestAddTrackClosureError,
          DisruptionActions.requestCompleteTrackClosureError,
          DisruptionActions.requestEditTagsError,
          DisruptionActions.requestDeleteSystemTagError,
          DisruptionActions.requestEditProtocolStepReactRoleError,
          DisruptionActions.requestEditProtocolStepManualFinishedDateError,
          DisruptionActions.requestEditProtocolStepExecuteResultError,
        ),
        filter((action) => !('silent' in action) || !action.silent),
        tap(({ type, error }) => {
          let errorTitle: string = null;

          switch (type) {
            case '[DISRUPTION] requestCreateItemError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.ADD_CARD.ERROR',
              );
              break;
            case '[DISRUPTION] requestUpdateItemError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.UPDATE.ERROR',
              );
              break;
            case '[DISRUPTION] requestAddPvsError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.ADD_PVS.ERROR',
              );
              break;
            case '[DISRUPTION] requestAddParentDisruptionError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.ADD_PARENT_DISRUPTION.ERROR',
              );
              break;
            case '[DISRUPTION] requestAddParentSecurityEventError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.ADD_PARENT_SECURITY_EVENT.ERROR',
              );
              break;
            case '[DISRUPTION] requestAddChildDisruptionError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.ADD_CHILD_DISRUPTION.ERROR',
              );
              break;
            case '[DISRUPTION] requestSetToWorkingError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.SET_TO_WORKING.ERROR',
              );
              break;
            case '[DISRUPTION] requestSetToCompletedError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.SET_TO_COMPLETED.ERROR',
              );
              break;
            case '[DISRUPTION] requestSetToCloseError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.SET_TO_CLOSE.ERROR',
              );
              break;
            case '[DISRUPTION] requestSetToCancelError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.SET_TO_CANCEL.ERROR',
              );
              break;
            case '[DISRUPTION] requestSetToArchiveError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.SET_TO_ARCHIVE.ERROR',
              );
              break;
            case '[DISRUPTION] requestFixCompensationRouteError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.FIX_COMPENSATION_ROUTE.ERROR',
              );
              break;
            case '[DISRUPTION] requestRunProtocolError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.RUN.ERROR',
              );
              break;
            case '[DISRUPTION] requestCancelProtocolError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.CANCEL.ERROR',
              );
              break;
            case '[DISRUPTION] requestAddProtocolStepError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.ADD_STEP.ERROR',
              );
              break;
            case '[DISRUPTION] requestProtocolStepsDeleteError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.DELETE_STEP.ERROR',
              );
              break;
            case '[DISRUPTION] requestProtocolStepsCompleteError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.COMPLETE_STEP.ERROR',
              );
              break;
            case '[DISRUPTION] requestProtocolStepsCancelError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.CANCEL_STEP.ERROR',
              );
              break;
            case '[DISRUPTION] requestEditProtocolStepReactRoleError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.EDIT_REACT_ROLE_STEP.ERROR',
              );
              break;
            case '[DISRUPTION] requestEditProtocolStepManualFinishedDateError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.EDIT_MANUAL_FINISH_DATE_STEP.ERROR',
              );
              break;
            case '[DISRUPTION] requestEditProtocolStepExecuteResultError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PROTOCOL_RUNNER.FUNCTIONS.EDIT_EXECUTE_STEP_RESULT.ERROR',
              );
              break;
            case '[DISRUPTION] requestSetPvsTaskToCancelError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PVS_CALL.FUNCTIONS.SET_TO_CANCEL.ERROR',
              );
              break;
            case '[DISRUPTION] requestSetPvsTaskToCompleteError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.PVS_CALL.FUNCTIONS.SET_TO_COMPLETED.ERROR',
              );
              break;
            case '[DISRUPTION] requestShowOnWallError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.SHOW_ON_WALL.ERROR',
              );
              break;
            case '[DISRUPTION] requestHideFromWallError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.HIDE_FROM_WALL.ERROR',
              );
              break;
            case '[DISRUPTION] requestAddTrackClosureError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.ADD_TRACK_CLOSURE.ERROR',
              );
              break;
            case '[DISRUPTION] requestCompleteTrackClosureError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.COMPLETE_TRACK_CLOSURE.ERROR',
              );
              break;
            case '[DISRUPTION] requestEditTagsError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.EDIT_TAGS.ERROR',
              );
              break;
            case '[DISRUPTION] requestDeleteSystemTagError':
              errorTitle = this.translateService.instant(
                'EVENT.DISRUPTION.CARD.FUNCTIONS.EDIT_TAGS.DELETE_SYSTEM_ERROR',
              );
              break;
            default:
              errorTitle = this.translateService.instant(
                'GENERAL.ERROR_REQUEST',
              );
              break;
          }

          this.toastService.add({
            level: 'error',
            summary: errorTitle,
            detail: parseError(error),
          });
        }),
      ),
    { dispatch: false },
  );

  readonly showErrorForDeleteChildDisruption$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DisruptionActions.requestDeleteChildDisruptionError),
        tap(({ error, eventLinkId }) => {
          let errorTitle: string = null;

          errorTitle =
            this.translateService.instant(
              'EVENT.DISRUPTION.CARD.FUNCTIONS.DELETE_CHILD_DISRUPTION.ERROR',
            ) + eventLinkId;

          this.toastService.add({
            level: 'error',
            summary: errorTitle,
            detail: parseError(error),
          });
        }),
      ),
    { dispatch: false },
  );
}
